
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "security-features-of-apache-atlas"
    }}>{`Security Features of Apache Atlas`}</h1>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`The following features are available for enhancing the security of the platform:`}</p>
    <ul>
      <li parentName="ul">{`SSL`}</li>
      <li parentName="ul">{`Service Authentication`}</li>
      <li parentName="ul">{`SPNEGO-based HTTP Authentication`}</li>
    </ul>
    <h2 {...{
      "id": "ssl"
    }}>{`SSL`}</h2>
    <p>{`Both SSL one-way (server authentication) and two-way (server and client authentication) are supported.  The following application properties (properties configured in the atlas-application.properties file) are available for configuring SSL:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.enableTLS`}</inlineCode>{` (false|true) `}{`[default: false]`}{` - enable/disable the SSL listener`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`keystore.file`}</inlineCode>{` - the path to the keystore file leveraged by the server.  This file contains the server certificate.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`truststore.file`}</inlineCode>{` - the path to the truststore file. This file contains the certificates of other trusted entities (e.g. the certificates for client processes if two-way SSL is enabled).  In most instances this can be set to the same value as the keystore.file property (especially if one-way SSL is enabled).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`client.auth.enabled`}</inlineCode>{` (false|true) `}{`[default: false]`}{` - enable/disable client authentication.  If enabled, the client will have to authenticate to the server during the transport session key creation process (i.e. two-way SSL is in effect).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`cert.stores.credential.provider.path`}</inlineCode>{` - the path to the Credential Provider store file.  The passwords for the keystore, truststore, and server certificate are maintained in this secure file.  Utilize the cputil script in the 'bin' directory (see below) to populate this file with the passwords required.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.ssl.exclude.cipher.suites`}</inlineCode>{` - the excluded Cipher Suites list -  `}<em parentName="li">{`NULL.`}</em>{`,.`}<em parentName="li">{`RC4.`}</em>{`,.`}<em parentName="li">{`MD5.`}</em>{`,.`}<em parentName="li">{`DES.`}</em>{`,.`}<em parentName="li">{`DSS.`}</em>{` are weak and unsafe Cipher Suites that are excluded by default. If additional Ciphers need to be excluded, set this property with the default Cipher Suites such as atlas.ssl.exclude.cipher.suites=.`}<em parentName="li">{`NULL.`}</em>{`, .`}<em parentName="li">{`RC4.`}</em>{`, .`}<em parentName="li">{`MD5.`}</em>{`, .`}<em parentName="li">{`DES.`}</em>{`, .`}<em parentName="li">{`DSS.`}</em>{`, and add the additional Cipher Suites to the list with a comma separator. They can be added with their full name or a regular expression. The Cipher Suites listed in the atlas.ssl.exclude.cipher.suites property will have precedence over the default Cipher Suites. One would keep the default Cipher Suites, and add additional ones to be safe.`}</li>
    </ul>
    <h4 {...{
      "id": "credential-provider-utility-script"
    }}>{`Credential Provider Utility Script`}</h4>
    <p>{`In order to prevent the use of clear-text passwords, the Atlas platofrm makes use of the Credential Provider facility for secure password storage (see `}<a parentName="p" {...{
        "href": "http://hadoop.apache.org/docs/current/hadoop-project-dist/hadoop-common/CommandsManual.html#credentia"
      }}>{`Hadoop Credential Command Reference`}</a>{` for more information about this facility).  The cputil script in the `}<inlineCode parentName="p">{`bin`}</inlineCode>{` directory can be leveraged to create the password store required.`}</p>
    <p>{`To create the credential provdier for Atlas:`}</p>
    <ul>
      <li parentName="ul">{`cd to the `}<inlineCode parentName="li">{`bin`}</inlineCode>{` directory`}</li>
      <li parentName="ul">{`type `}<inlineCode parentName="li">{`./cputil.py`}</inlineCode></li>
      <li parentName="ul">{`Enter the path for the generated credential provider.  The format for the path is:`}
        <ul parentName="li">
          <li parentName="ul">{`jceks://file/local/file/path/file.jceks or jceks://hdfs@namenodehost:port/path/in/hdfs/to/file.jceks.  The files generally use the ".jceks" extension (e.g. test.jceks)`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Enter the passwords for the keystore, truststore, and server key (these passwords need to match the ones utilized for actually creating the associated certificate store files).`}</li>
    </ul>
    <p>{`The credential provider will be generated and saved to the path provided.`}</p>
    <h2 {...{
      "id": "service-authentication"
    }}>{`Service Authentication`}</h2>
    <p>{`The Atlas platform, upon startup, is associated to an authenticated identity.  By default, in an insecure environment, that identity is the same as the OS authenticated user launching the server.  However, in a secure cluster leveraging kerberos, it is considered a best practice to configure a keytab and principal in order for the platform to authenticate to the KDC. This allows the service to subsequently interact with other secure cluster services (e.g. HDFS).`}</p>
    <p>{`The properties for configuring service authentication are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.authentication.method`}</inlineCode>{` (simple|kerberos) `}{`[default: simple]`}{` - the authentication method to utilize.  Simple will leverage the OS authenticated identity and is the default mechanism.  'kerberos' indicates that the service is required to authenticate to the KDC leveraging the configured keytab and principal.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.authentication.keytab`}</inlineCode>{` - the path to the keytab file.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.authentication.principal`}</inlineCode>{` - the principal to use for authenticating to the KDC.  The principal is generally of the form "user/host@realm".  You may use the '_HOST' token for the hostname and the local hostname will be substituted in by the runtime (e.g. "Atlas/`}<a parentName="li" {...{
          "href": "mailto:_HOST@EXAMPLE.COM"
        }}>{`_HOST@EXAMPLE.COM`}</a>{`")`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote">{`Note that when Atlas is configured with HBase as the storage backend in a secure cluster, the graph db (JanusGraph) needs sufficient user permissions to be able to create and access an HBase table.  To grant the appropriate permissions see `}<a parentName="p" {...{
          "href": "#/Configuration"
        }}>{`Graph persistence engine - Hbase`}</a>{`.`}</p>
    </blockquote>
    <h3 {...{
      "id": "jaas-configuration"
    }}>{`JAAS configuration`}</h3>
    <p>{`In a secure cluster, some components (such as Kafka) that Atlas interacts with, require Atlas to authenticate itself to them using JAAS. The following properties are used to set up appropriate JAAS Configuration.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.loginModuleName`}</inlineCode>{` - the authentication method used by the component (for example, com.sun.security.auth.module.Krb5LoginModule)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.loginModuleControlFlag`}</inlineCode>{` (required|requisite|sufficient|optional) `}{`[default: required]`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.option.useKeyTab`}</inlineCode>{` (true|false)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.option.storeKey`}</inlineCode>{` (true | false)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.option.serviceName`}</inlineCode>{` - service name of server component`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.option.keyTab`}</inlineCode>{` = `}<inlineCode parentName="li">{`<atlas keytab>`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.jaas.client-id.option.principal`}</inlineCode>{` = `}<inlineCode parentName="li">{`<atlas principal>`}</inlineCode></li>
    </ul>
    <p>{`For example, the following property settings in jaas-application.properties file`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.jaas.KafkaClient.loginModuleName = com.sun.security.auth.module.Krb5LoginModule
atlas.jaas.KafkaClient.loginModuleControlFlag = required
atlas.jaas.KafkaClient.option.useKeyTab = true
atlas.jaas.KafkaClient.option.storeKey = true
atlas.jaas.KafkaClient.option.serviceName = kafka
atlas.jaas.KafkaClient.option.keyTab = /etc/security/keytabs/kafka_client.keytab
atlas.jaas.KafkaClient.option.principal = kafka-client-1@EXAMPLE.COM
atlas.jaas.MyClient.0.loginModuleName = com.sun.security.auth.module.Krb5LoginModule
atlas.jaas.MyClient.0.loginModuleControlFlag = required
atlas.jaas.MyClient.0.option.useKeyTab = true
atlas.jaas.MyClient.0.option.storeKey = true
atlas.jaas.MyClient.0.option.serviceName = kafka
atlas.jaas.MyClient.0.option.keyTab = /etc/security/keytabs/kafka_client.keytab
atlas.jaas.MyClient.0.option.principal = kafka-client-1@EXAMPLE.COM
atlas.jaas.MyClient.1.loginModuleName = com.sun.security.auth.module.Krb5LoginModule
atlas.jaas.MyClient.1.loginModuleControlFlag = optional
atlas.jaas.MyClient.1.option.useKeyTab = true
atlas.jaas.MyClient.1.option.storeKey = true
atlas.jaas.MyClient.1.option.serviceName = kafka
atlas.jaas.MyClient.1.option.keyTab = /etc/security/keytabs/kafka_client.keytab
atlas.jaas.MyClient.1.option.principal = kafka-client-1@EXAMPLE.COM`}
    </SyntaxHighlighter>
    <p>{`will set the JAAS configuration that is equivalent to the following jaas.conf file entries.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`KafkaClient {
   com.sun.security.auth.module.Krb5LoginModule required
   useKeyTab=true
   storeKey=true
   serviceName=kafka
   keyTab="/etc/security/keytabs/kafka_client.keytab"
   principal="kafka-client-1@EXAMPLE.COM";
};
MyClient {
   com.sun.security.auth.module.Krb5LoginModule required
   useKeyTab=true
   storeKey=true
   serviceName=kafka keyTab="/etc/security/keytabs/kafka_client.keytab"
   principal="kafka-client-1@EXAMPLE.COM";
};
MyClient {
   com.sun.security.auth.module.Krb5LoginModule optional
   useKeyTab=true
   storeKey=true
   serviceName=kafka
   keyTab="/etc/security/keytabs/kafka_client.keytab"
   principal="kafka-client-1@EXAMPLE.COM";
};`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "spnego-based-http-authentication"
    }}>{`SPNEGO-based HTTP Authentication`}</h2>
    <p>{`HTTP accesses to the Atlas platform can be secured by enabling the platform's SPNEGO support.  There are currently two supported authentication mechanisms:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`simple`}</inlineCode>{` - authentication is performed via a provided username`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`kerberos`}</inlineCode>{` - the KDC authenticated identity of the client is leveraged to authenticate to the server`}</li>
    </ul>
    <p>{`The kerberos support requires the client accessing the server to first authenticate to the KDC (usually this is done via the 'kinit' command).  Once authenticated, the user may access the server (the authenticated identity will be related to the server via the SPNEGO negotiation mechanism).`}</p>
    <p>{`The properties for configuring the SPNEGO support are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.http.authentication.enabled`}</inlineCode>{` (true|false) `}{`[default: false]`}{` - a property indicating whether to enable HTTP authentication`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.http.authentication.type`}</inlineCode>{` (simple|kerberos) `}{`[default: simple]`}{` - the authentication type`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.http.authentication.kerberos.principal`}</inlineCode>{` - the web-application Kerberos principal name. The Kerberos principal name must start with "HTTP/...". For example: "HTTP/localhost@LOCALHOST". There is no default value.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.http.authentication.kerberos.keytab`}</inlineCode>{` - the path to the keytab file containing the credentials for the kerberos principal.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.rest.address`}</inlineCode>{` - `}<inlineCode parentName="li">{`<http/https>://<atlas-fqdn>:<atlas port>`}</inlineCode></li>
    </ul>
    <p>{`For a more detailed discussion of the HTTP authentication mechanism refer to `}<a parentName="p" {...{
        "href": "http://hadoop.apache.org/docs/stable/hadoop-auth/Configuration.html"
      }}>{`Hadoop Auth, Java HTTP SPNEGO 2.6.0 - Server Side Configuration`}</a>{`.  The prefix that document references is "atlas.http.authentication" in the case of the Atlas authentication implementation.`}</p>
    <h3 {...{
      "id": "client-security-configuration"
    }}>{`Client security configuration`}</h3>
    <p>{`When leveraging Atlas client code to communicate with an Atlas server configured for SSL transport and/or Kerberos authentication, there is a requirement to provide the Atlas client configuration file that provides the security properties that allow for communication with, or authenticating to, the server.`}</p>
    <p>{`Update the atlas-application.properties file with the appropriate settings (see below) and copy it to the client's classpath or to the directory specified by the "atlas.conf" system property.`}</p>
    <p>{`The client properties for SSL communication are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.enableTLS`}</inlineCode>{` (false|true) `}{`[default: false]`}{` - enable/disable the SSL client communication infrastructure.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`keystore.file`}</inlineCode>{` - the path to the keystore file leveraged by the client.  This file is only required if 2-Way SSL is enabled at the server and contains the client certificate.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`truststore.file`}</inlineCode>{` - the path to the truststore file. This file contains the certificates of trusted entities (e.g. the certificates for the server or a shared certification authority). This file is required for both one-way or two-way SSL.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`cert.stores.credential.provider.path`}</inlineCode>{` - the path to the Credential Provider store file.  The passwords for the keystore, truststore, and client certificate are maintained in this secure file.`}</li>
    </ul>
    <p>{`The property required for authenticating to the server (if authentication is enabled):`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`atlas.http.authentication.type`}</inlineCode>{` (simple|kerberos) `}{`[default: simple]`}{` - the authentication type`}</li>
    </ul>
    <h3 {...{
      "id": "solr-kerberos-configuration"
    }}>{`SOLR Kerberos configuration`}</h3>
    <p>{`If the authentication type specified is 'kerberos', then the kerberos ticket cache will be accessed for authenticating to the server (Therefore the client is required to authenticate to the KDC prior to communication with the server using 'kinit' or a similar mechanism).`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://cwiki.apache.org/confluence/display/RANGER/How+to+configure+Solr+Cloud+with+Kerberos+for+Ranger+0.5"
      }}>{`the Apache SOLR Kerberos configuration`}</a>{`.`}</p>
    <ul>
      <li parentName="ul">{`Add principal and generate the keytab file for solr.  Create a keytab per host for each host where Solr is going to run and use the principal name with the host (e.g. addprinc -randkey solr/\${HOST1}@EXAMPLE.COM. Replace \${HOST1} with the actual host names).`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`kadmin.local
kadmin.local:  addprinc -randkey solr/<hostname>@EXAMPLE.COM
kadmin.local:  xst -k solr.keytab solr/<hostname>@EXAMPLE.COM
kadmin.local:  quit`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Add principal and generate the keytab file for authenticating HTTP request. (Note that if Ambari is used to Kerberize the cluster, the keytab /etc/security/keytabs/spnego.service.keytab can be used)`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`kadmin.local
kadmin.local:  addprinc -randkey HTTP/<hostname>@EXAMPLE.COM
kadmin.local:  xst -k HTTP.keytab HTTP/<hostname>@EXAMPLE.COM
kadmin.local:  quit`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Copy the keytab file to all the hosts running Solr.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`cp solr.keytab /etc/security/keytabs/
chmod 400 /etc/security/keytabs/solr.keytab
cp HTTP.keytab /etc/security/keytabs/
chmod 400 /etc/security/keytabs/HTTP.keytab`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Create path in Zookeeper for storing the Solr configs and other parameters.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`$SOLR_INSTALL_HOME/server/scripts/cloud-scripts/zkcli.sh -zkhost $ZK_HOST:2181 -cmd makepath solr`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Upload the configuration to Zookeeper.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`$SOLR_INSTALL_HOME/server/scripts/cloud-scripts/zkcli.sh -cmd upconfig  -zkhost $ZK_HOST:2181/solr -confname basic_configs -confdir $SOLR_INSTALL_HOME/server/solr/configsets/_default/conf`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Create the JAAS configuration.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`vi /etc/solr/conf/solr_jaas.conf
Client {
  com.sun.security.auth.module.Krb5LoginModule required
  useKeyTab=true
  keyTab="/etc/security/keytabs/solr.keytab"
  storeKey=true
  useTicketCache=true
  debug=true
  principal="solr/<hostname>@EXAMPLE.COM";
};`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Copy /etc/solr/conf/solr_jaas.conf to all hosts running Solr.`}</li>
      <li parentName="ul">{`Edit solr.in.sh in $SOLR_INSTALL_HOME/bin/`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`vi $SOLR_INSTALL_HOME/bin/solr.in.sh
SOLR_JAAS_FILE=/etc/solr/conf/solr_jaas.conf
SOLR_HOST='hostname -f'
ZK_HOST="$ZK_HOST1:2181,$ZK_HOST2:2181,$ZK_HOST3:2181/solr"
KERBEROS_REALM="EXAMPLE.COM"
SOLR_KEYTAB=/etc/solr/conf/solr.keytab
SOLR_KERB_PRINCIPAL=HTTP@{KERBEROS_REALM}
SOLR_KERB_KEYTAB=/etc/solr/conf/HTTP.keytab
SOLR_AUTHENTICATION_CLIENT_CONFIGURER="org.apache.solr.client.solrj.impl.Krb5HttpClientConfigurer"
SOLR_AUTHENTICATION_OPTS=" -DauthenticationPlugin=org.apache.solr.security.KerberosPlugin -Djava.security.auth.login.config={SOLR_JAAS_FILE} -Dsolr.kerberos.principal={SOLR_KERB_PRINCIPAL} -Dsolr.kerberos.keytab={SOLR_KERB_KEYTAB} -Dsolr.kerberos.cookie.domain={SOLR_HOST} -Dhost={SOLR_HOST} -Dsolr.kerberos.name.rules=DEFAULT"`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Copy solr.in.sh to all hosts running Solr.`}</li>
      <li parentName="ul">{`Set up Solr to use the Kerberos plugin by uploading the security.json.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`$SOLR_INSTALL_HOME/server/scripts/cloud-scripts/zkcli.sh -zkhost <zk host>:2181 -cmd put /security.json '{"authentication":{"class": "org.apache.solr.security.KerberosPlugin"}}'`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Start Solr.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`$SOLR_INSTALL_HOME/bin/solr start -cloud -z $ZK_HOST1:2181,$ZK_HOST2:2181,$ZK_HOST3:2181 -noprompt`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Test Solr`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`kinit -k -t /etc/security/keytabs/HTTP.keytab HTTP/<host>@EXAMPLE.COM
curl --negotiate -u : "http://<host>:8983/solr/"`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Create collections in Solr corresponding to the indexes that Atlas uses and change the Atlas configuration to point to the Solr instance setup as described in the `}<a parentName="li" {...{
          "href": "#/Installation"
        }}>{`Install Steps`}</a></li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;